// @ts-check

/** @file Privacy-safe TensorBuzz reporting seam for project sync failures/conflicts. */

import {classifyProjectSyncError} from "../sync/project-sync-error.js"

const AUTHENTICATION_TRANSITION_CODE = "authentication-required"

/**
 * Reports an unexpected background-cycle error without its message, stack, or payload.
 * The sink deliberately never throws back into detached coordinator work.
 * @param {Error} error Unexpected framework failure.
 * @returns {void}
 */
export function reportProjectSyncError(error) {
  const failure = classifyProjectSyncError(error)

  if (isAuthenticationTransition(failure)) return
  if (typeof reportError !== "function") return
  try {
    reportError(new Error(`Project sync background failure: ${JSON.stringify({code: failure.code, retryable: failure.retryable})}`))
  } catch (_reportingError) {
    console.warn("Could not report project sync background failure")
  }
}

/**
 * Reports identifiers, classifications, counts, and scalar versions only.
 * Local/server payloads and caught transport messages never enter the report.
 * @param {{projectId: string, status: import("velocious/build/src/sync/sync-coordinator-types.js").SyncCoordinatorStatus, userId: string}} args Safe status context.
 */
export function reportProjectSyncStatus({projectId, status, userId}) {
  if (isAuthenticationTransition(status.failure)) return
  if (typeof reportError !== "function") return
  const details = {
    conflicts: status.conflicts.map(({baseVersion, localVersion, resourceId, resourceType, serverVersion, versionAttribute}) => ({
      baseVersion,
      localVersion,
      resourceId,
      resourceType,
      serverVersion,
      versionAttribute
    })),
    failureCode: status.failure?.code ?? null,
    pendingCount: status.pendingCount,
    projectId,
    rejectedCount: status.rejectedCount,
    state: status.state,
    userId
  }

  try {
    reportError(new Error(`Project sync diagnostic: ${JSON.stringify(details)}`))
  } catch (_reportingError) {
    console.warn("Could not report project sync diagnostic")
  }
}

/**
 * Keeps expected session teardown/expiry out of the unhandled-error channel.
 * The lifecycle still publishes the status for product UI state.
 * @param {{code: string} | null | undefined} failure Safe failure metadata.
 * @returns {boolean} Whether authentication lifecycle owns the transition.
 */
function isAuthenticationTransition(failure) {
  return failure?.code === AUTHENTICATION_TRANSITION_CODE
}
